import { flatten, some, every, compact } from 'lodash';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { IReduxState } from 'store/store';
import { TFlagKey } from 'types';

type TFlagHookArguments = TFlagKey[] | TFlagKey[][];

/**
 * Test a list of feature flags in the redux store to see if *ALL* of the list are enabled.
 *
 * @returns IFeatureFlags
 *
 * @example
 *
 * const MANAGEMENT_LISTS = useFeatureFlags('MANAGEMENT_LISTS');
 * const isAllEnabled = useFeatureFlags('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES');
 * const isAllEnabledAlt = useFeatureFlags(['MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES']);
 */
export const useFeatureFlags = (...flagKeys: TFlagHookArguments) => {
  const flatFlags: TFlagKey[] = compact(flatten(flagKeys));

  return useSelector<IReduxState, boolean>((state): boolean => {
    const { features } = state;

    return every(flatFlags, (key) => features[key]);
  });
};

export const useTextFeatureFlag = (flagKey: TFlagKey) => {
  return useSelector<IReduxState, string>((state): string => {
    const { features } = state;

    return features[flagKey] as string;
  });
};

/**
 * Test a list of feature flags in the redux store, to see if *ANY* of the features are enabled.
 *
 * @returns IFeatureFlags
 *
 * @example
 *
 * const isAnyEnabled = useAnyFeatureFlags('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES');
 * const isAnyEnabledAlt = useAnyFeatureFlags(['MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES']);
 */
export const useAnyFeatureFlags = (...flagKeys: TFlagHookArguments) => {
  const flatFlags: TFlagKey[] = compact(flatten(flagKeys));

  return useSelector<IReduxState, boolean>((state): boolean => {
    const { features } = state;

    return some(flatFlags, (key) => features[key]);
  });
};

/**
 * Test a list of feature flags in the redux store, to see if *NONE* of the features are enabled.
 *
 * @returns IFeatureFlags
 *
 * @example
 *
 * const isNoneEnabled = useWithoutFeatureFlags('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES');
 * const isNoneEnabledAlt = useWithoutFeatureFlags(['MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES']);
 */
export const useWithoutFeatureFlags = (...flagKeys: TFlagHookArguments) => !useAnyFeatureFlags(...flagKeys);

/**
 * Returns a getter for the status of each flag in a list individually (as a dictionary
 * keyed by flag name), instead of collapsing them into one ALL-enabled boolean like
 * useFeatureFlags does. Safe to call inside loops/maps/handlers, since the redux
 * subscription happens once here rather than once per flag-key-list.
 *
 * @example
 *
 * const getFeatureFlagCollectionStatus = useGetFeatureFlagCollectionStatus();
 * const flagStatus = getFeatureFlagCollectionStatus('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES');
 * const flagStatusAlt = getFeatureFlagCollectionStatus(['MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES']);
 */
export const useGetFeatureFlagCollectionStatus = () => {
  const features = useSelector((state: IReduxState) => state.features);

  return useCallback(
    (...flagKeys: TFlagHookArguments): Partial<Record<TFlagKey, boolean>> => {
      const flatFlags: TFlagKey[] = compact(flatten(flagKeys));

      return Object.fromEntries(flatFlags.map((key) => [key, Boolean(features[key])]));
    },
    [features]
  );
};

/**
 * Returns the status of each flag in a list individually, as a dictionary keyed by
 * flag name - see useGetFeatureFlagCollectionStatus for the getter version.
 *
 * @example
 *
 * const flagStatus = useFeatureFlagCollectionStatus('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES');
 * const flagStatusAlt = useFeatureFlagCollectionStatus(['MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES']);
 */
export const useFeatureFlagCollectionStatus = (
  ...flagKeys: TFlagHookArguments
): Partial<Record<TFlagKey, boolean>> => {
  const getFeatureFlagCollectionStatus = useGetFeatureFlagCollectionStatus();

  return getFeatureFlagCollectionStatus(...flagKeys);
};

/**
 * Returns a getter for testing whether a list of feature flags is *ALL* enabled, instead
 * of a computed boolean directly like useFeatureFlags - safe to call inside
 * loops/maps/handlers, since the redux subscription happens once here rather than once
 * per flag-key-list.
 *
 * @example
 *
 * const getFeatureFlagStatus = useGetFeatureFlagStatus();
 * const isAllEnabled = getFeatureFlagStatus('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES');
 * const isAllEnabledAlt = getFeatureFlagStatus(['MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EMPLOYEES']);
 */
export const useGetFeatureFlagStatus = () => {
  const getFeatureFlagCollectionStatus = useGetFeatureFlagCollectionStatus();

  return useCallback(
    (...flagKeys: TFlagHookArguments) => every(getFeatureFlagCollectionStatus(...flagKeys)),
    [getFeatureFlagCollectionStatus]
  );
};
